import { useEffect } from "react";

interface PageSEOOptions {
    title: string;
    description: string;
    keywords?: string;
    canonicalPath?: string;
    ogTitle?: string;
    ogDescription?: string;
    structuredData?: object;
}

/**
 * Hook to dynamically set per-page SEO meta tags.
 * Updates document.title (Chrome tab tooltip), meta description,
 * keywords, Open Graph tags, canonical URL, and JSON-LD structured data.
 */
export function usePageSEO({
    title,
    description,
    keywords,
    canonicalPath,
    ogTitle,
    ogDescription,
    structuredData,
}: PageSEOOptions) {
    useEffect(() => {
        // Set document title (the Chrome tab hover text)
        document.title = title;

        // Helper to set or create a meta tag
        const setMeta = (attr: string, key: string, content: string) => {
            let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };

        // Meta description
        setMeta("name", "description", description);

        // Keywords
        if (keywords) {
            setMeta("name", "keywords", keywords);
        }

        // Open Graph
        setMeta("property", "og:title", ogTitle || title);
        setMeta("property", "og:description", ogDescription || description);
        if (canonicalPath) {
            const baseUrl = "https://selectcleaning.co.nz";
            const fullUrl = `${baseUrl}${canonicalPath}`;
            setMeta("property", "og:url", fullUrl);

            // Canonical link
            let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
            if (!canonical) {
                canonical = document.createElement("link");
                canonical.setAttribute("rel", "canonical");
                document.head.appendChild(canonical);
            }
            canonical.setAttribute("href", fullUrl);
        }

        // JSON-LD Structured Data
        let scriptTag: HTMLScriptElement | null = null;
        if (structuredData) {
            scriptTag = document.createElement("script");
            scriptTag.type = "application/ld+json";
            scriptTag.textContent = JSON.stringify(structuredData);
            document.head.appendChild(scriptTag);
        }

        // Cleanup structured data on unmount
        return () => {
            if (scriptTag && scriptTag.parentNode) {
                scriptTag.parentNode.removeChild(scriptTag);
            }
        };
    }, [title, description, keywords, canonicalPath, ogTitle, ogDescription, structuredData]);
}
